import { useEffect, useRef } from 'react';

import { Loader, LOADER_SIZE } from '@amzn/sss-website-theme';

const isValidRedirectUrl = (url: string): boolean => {
  try {
    // Resolve the URL against the current origin using the URL parser.
    // This handles all edge cases including backslash normalization (/\evil.com),
    // protocol-relative URLs (//evil.com), tab/newline injection, and other
    // browser-specific URL parsing behaviors per the WHATWG URL spec.
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    // Invalid URL format
    return false;
  }
};

/**
 * The /sign-in page is protected by Mons authentication. If a user reaches this page,
 * they have already completed the Mons auth round-trip and are guaranteed to be
 * authenticated. This component simply reads the `redirectTo` query parameter and
 * redirects the user to the appropriate page.
 *
 * We intentionally do NOT use the useGetUserRights hook here because:
 * - Session storage may contain stale user rights from a prior unauthenticated page visit
 *   (e.g., UNAUTHENTICATED cached when the user was on a segment page before signing in).
 * - The trim does not run on /sign-in, so polling session storage or clearing it would
 *   result in an indefinite loading state.
 * - Mons authentication is the source of truth for auth status on this page, not ACPS.
 */
export const SignInPage = () => {
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (hasRedirected.current) return;

    hasRedirected.current = true;
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = urlParams.get('redirectTo');

    if (redirectTo) {
      try {
        const decodedRedirectUrl = decodeURIComponent(redirectTo);

        // Validate that the redirect URL is from the same origin
        if (!isValidRedirectUrl(decodedRedirectUrl)) {
          console.warn('Invalid redirectTo parameter: URL must be from the same origin');
          window.location.assign('/');
          return;
        }

        urlParams.delete('redirectTo');

        const newUrl = `${window.location.origin}${window.location.pathname}${
          urlParams.toString() ? `?${urlParams.toString()}` : ''
        }${window.location.hash}`;

        window.history.replaceState({}, '', newUrl);
        window.location.assign(decodedRedirectUrl);
      } catch (error) {
        console.error('Error processing redirectTo parameter:', error);
        window.location.assign('/');
      }
    } else {
      window.location.assign('/');
    }
  }, []);

  return <Loader size={LOADER_SIZE.LARGE} />;
};
